/**
 * ApStateMapperPicker  (SM-23 / DA-2296)
 *
 * Choiceless NUMBER/2D/3D AP questions: the question's `stateMapper` *is* the whole mapper
 * and takes the form `AP:<id>-<op>[:<attrKey>]`. Per the grammar (§3.3) only C (count) and
 * A (attribute) are legal for the AP no-choice path — T/S are not read/written there — so
 * the operator is limited to those two. Cascade: appliance → operator → (for A) attribute key.
 *
 * Like HpStateMapperPicker this is single-slot; 2D/3D comma-joined multi-attribute state
 * mappers are a shared follow-up on both pickers.
 */
import React, { useEffect, useRef, useState } from 'react'
import { Box, SxProps, Theme } from '@mui/material'
import { SearchableSelect } from 'components/common/SearchableSelect'
import { MapperCatalog } from 'types/apis/surveyInterfaces'
import { applianceOptions } from './apCatalog'
import { StateMapperPreview } from './StateMapperPreview'

export interface ApStateMapperPickerProps {
  value?: string
  onChange: (stateMapper: string) => void
  catalog?: MapperCatalog
  loading?: boolean
  /** Catalog fetch failure, surfaced inline so it can't be misread as "no appliances". */
  error?: string
  disabled?: boolean
  /** SM-25: modified-field highlight, same treatment as the raw field this replaces. */
  sx?: SxProps<Theme>
}

// AP:<id>-<op>[:<attrKey>]  (choiceless AP: op is C or A only)
const AP_STATE_RE = /^AP:(\d+)-([CA])(?::(.+))?$/
const CHOICELESS_AP_OPERATORS = ['C', 'A']

function parse(value?: string): { applianceId?: number; operator?: string; attributeKey?: string } {
  const m = value ? AP_STATE_RE.exec(value) : null
  if (!m) return {}
  return { applianceId: Number(m[1]), operator: m[2], attributeKey: m[3] }
}

function build(applianceId?: number, operator?: string, attributeKey?: string): string {
  if (applianceId === undefined || !operator) return ''
  let s = `AP:${applianceId}-${operator}`
  if (operator === 'A' && attributeKey) s += `:${attributeKey}`
  return s
}

export const ApStateMapperPicker: React.FC<ApStateMapperPickerProps> = ({ value, onChange, catalog, loading, error, disabled, sx }) => {
  // The cascade only becomes a legal mapper once appliance AND operator are both set, so
  // a half-finished pick has nowhere to live in `value` — build() serializes it to ''.
  // Deriving the controls straight off `value` therefore erased the appliance the instant
  // it was chosen and left the operator step permanently disabled. Hold the in-progress
  // selection locally and let `value` carry only the complete result.
  const [draft, setDraft] = useState(() => parse(value))
  const emitted = useRef<string | undefined>(undefined)

  // Re-seed on a genuine external change (form reset, switching question, reload) while
  // ignoring the echo of our own onChange — that echo is '' for an incomplete cascade and
  // would wipe the draft on every intermediate step.
  useEffect(() => {
    if (value === emitted.current) return
    setDraft(parse(value))
  }, [value])

  const { applianceId, operator, attributeKey } = draft

  const update = (next: { applianceId?: number; operator?: string; attributeKey?: string }) => {
    setDraft(next)
    const built = build(next.applianceId, next.operator, next.attributeKey)
    emitted.current = built
    onChange(built)
  }

  const appliances = applianceOptions(catalog?.appliances)
  const operatorOptions = (catalog?.rules.ap.operators ?? [])
    .filter((o) => CHOICELESS_AP_OPERATORS.includes(o.code))
    .map((o) => ({ value: o.code, label: o.label }))
  const attrKeyOptions = (catalog?.applianceAttributes ?? [])
    .filter((a) => a.applianceId === applianceId || a.applianceId === null)
    .map((a) => ({ value: a.attributeKey, label: a.attributeKey }))

  const appliancesEmpty = !loading && !error && appliances.length === 0
  // The catalog error/empty message rides on the primary (appliance) select so MUI links it via
  // aria-describedby — announced on focus, unlike the old sibling FormHelperText.
  const applianceHelperText = error || (appliancesEmpty ? 'No appliances available for this account type' : undefined)

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', ...(sx as any) }}>
        <SearchableSelect
          label="Appliance"
          value={applianceId !== undefined ? String(applianceId) : ''}
          // Changing the appliance invalidates the attribute key — the A-whitelist is
          // per-appliance — but the operator stays a valid choice.
          onChange={(v) => update({ applianceId: v ? Number(v) : undefined, operator, attributeKey: undefined })}
          options={appliances}
          disabled={disabled}
          loading={loading}
          error={!!error}
          helperText={applianceHelperText}
          noOptionsText={error ? 'Couldn’t load catalog' : 'No appliances available'}
          placeholder={loading ? 'Loading catalog…' : 'Select an appliance'}
        />
        <SearchableSelect
          label="Operator"
          value={operator ?? ''}
          onChange={(v) => update({ applianceId, operator: v || undefined, attributeKey: undefined })}
          options={operatorOptions}
          disabled={disabled || applianceId === undefined}
          placeholder="Count or Attribute"
        />
        {operator === 'A' && (
          <SearchableSelect
            label="Attribute key"
            value={attributeKey ?? ''}
            onChange={(v) => update({ applianceId, operator, attributeKey: v || undefined })}
            options={attrKeyOptions}
            disabled={disabled || applianceId === undefined}
            loading={loading}
            noOptionsText="No attribute keys for this appliance"
            placeholder={loading ? 'Loading…' : 'Select a key'}
          />
        )}
      </Box>
      <StateMapperPreview value={build(applianceId, operator, attributeKey)} />
    </>
  )
}
